// Provider stack
import { AppCalendarProvider } from "#app/components/app-calendar-provider";
import { AppInteractionPreferencesProvider } from "#app/components/app-interaction-preferences";
import { AppToastProvider } from "#app/components/app-toast-provider";
import { AnalyticsProvider } from "#app/lib/analytics";
import { DataProvider } from "#app/lib/data/data-provider";
import { AppProvider } from "#app/lib/data/app-provider";
import { MemberDirectoryProvider } from "#app/lib/data/member-directory-provider";
import { PresenceProvider } from "#app/lib/data/presence-provider";
import { WorkspaceDataProvider } from "#app/lib/data/workspace-data-provider";
import { SubscriptionProvider } from "#app/lib/subscriptions/subscription-provider";
import { composeProviders } from "#app/lib/compose-providers";
import { EntityDetailProvider } from "#app/components/entity";
import { GlobalSearchProvider } from "#app/components/global-search/global-search-context";
import { ModuleRailProvider } from "#app/components/navigation/module-rail";
import { ThemeAppearanceProvider } from "#app/components/theme/theme-appearance-provider";
import { IssueViewProvider } from "#app/components/issue-view";
import { DetailStackProvider } from "#app/components/detail-view/detail-stack";
import { DetailDrawerWidthProvider } from "#app/components/detail-view/detail-drawer-width";
import { TooltipProvider } from "@salestrak/ui";

// IMPORTANT — a Base UI Portal moves only the DOM node: React context reaches
// drawer/dialog content from where the overlay is RENDERED in the tree. Any
// Provider an overlay's content calls must sit ABOVE the component that
// renders that overlay.
// Overlay-mounting components live in the workspace layout, below this whole
// stack — so keep data/state providers here and render overlays there.
//
// COROLLARY: providers listed here must NOT render a `Drawer` (or a
// `useIsMobile()` branch that renders one) inline. Split context from
// render: keep the Provider here, and render the overlay inside the
// workspace layout. See `IssueViewDialogs` / `CustomFieldsDialog` for the
// pattern.

export const AppProviders = composeProviders([
  // ONE shared tooltip delay group for the whole app. Base UI roots work
  // without a provider but each then pays the full default delay in
  // isolation — the provider is what makes adjacent tooltips open instantly
  // while scanning (its `timeout` skip window). `delay` is the rest-before-
  // label wait; `timeout` is how long after a bubble closes the next one
  // still opens instantly. Pointer travel between adjacent controls is well
  // under 300ms, so scanning a row chains labels, while a pause of the same
  // length re-arms the full delay — a longer window kept flashing labels on
  // controls the pointer was merely crossing. No data deps, so it sits at
  // the very top.
  [TooltipProvider, { delay: 300, timeout: 300 }],
  [AnalyticsProvider],
  [DataProvider],
  // Apollo-dependent (reads the accent + neutral-shift preferences) → must sit
  // inside DataProvider. High in the stack so the custom properties it writes
  // to <html> are on the document before anything below it paints.
  [ThemeAppearanceProvider],
  // Apollo-dependent (reads the reduceMotion + pointerCursors preferences) →
  // must sit inside DataProvider. Stamps the motion/cursor attributes on
  // <html> the same way the appearance provider writes its properties.
  [AppInteractionPreferencesProvider],
  [AppProvider],
  // Ref-counted registry for every GraphQL subscription (needs the Apollo
  // client from DataProvider). Anything using useRegisterSubscriptions sits
  // below this.
  [SubscriptionProvider],
  [WorkspaceDataProvider],
  // Reads useWorkspaceData (members to poll) — must sit below it. Every
  // user avatar (EntityVisual) reads the statuses it provides.
  [PresenceProvider],
  // Also reads useWorkspaceData (to skip ids the bootstrap page already
  // answers) → below it. Resolves member ids OUTSIDE that page, so anything
  // that words a member id sits below this.
  [MemberDirectoryProvider],
  // Z-orders the three detail overlays below as one stack: a top-level open
  // (favorites, a table cell) clears the other kinds, a drill-in from inside a
  // drawer pushes. Sits above them because their public hooks read it.
  [DetailStackProvider],
  // The open detail drawer publishes its rendered width here; the page under
  // it (the map's action cluster and camera) reads it. No data deps — it
  // sits above the overlay hosts and the routes alike.
  [DetailDrawerWidthProvider],
  [IssueViewProvider],
  // Must sit below IssueViewProvider (it delegates the issue kind to it) and
  // above anything that renders a portaled overlay calling it.
  [EntityDetailProvider],
  // Reads the profile island's presence from EntityDetailProvider → below it.
  [GlobalSearchProvider],
  [ModuleRailProvider],
  // Apollo-dependent (reads the toastPosition preference) → must sit inside
  // DataProvider. Mounts the ONE toast viewport for the whole app.
  [AppToastProvider],
  // Apollo-dependent (reads the firstDayOfWeek preference) → must sit inside
  // DataProvider. Every Calendar/DatePicker below inherits the week start.
  [AppCalendarProvider],
]);
